import {
  hasTmaInitData,
  waitForTmaInitData,
} from '@common/services/tmaInitDataService';
import { fetcher } from '@common/utils/fetcher';

import {
  type PostStartTmaResponse,
  type StartTmaFeatures,
} from '@services/AuthService';

const TMA_APP_INSTANCE_NAME = 'kiss-date-tma';
const APP_INSTANCE_ID_STORAGE_KEY = 'app_instance_id';

let appInstanceIdCache: string | null = null;
let startPromise: Promise<string> | null = null;

const START_FEATURES_STORAGE_KEY = 'start_features';

/** How long the sign-up waits for flags it has never had. */
export const START_FEATURES_WAIT_MS = 2000;

/**
 * The global feature flags of this session's `POST /start/tma` answer:
 * `undefined` until an answer arrived, `null` when it carried none (an older
 * backend). They are pre-auth values (no testers: there is no user yet), so
 * they live here, apart from the account store's per-user features, which
 * they must never overwrite. The last answer is also kept in localStorage,
 * so a launch that skips `/start/tma` (stored instance id) still has them.
 */
let startFeatures: StartTmaFeatures | null | undefined;

const isStartFeatures = (value: unknown): value is StartTmaFeatures =>
  typeof value === 'object' &&
  value !== null &&
  typeof Reflect.get(value, 'matchmaker') === 'boolean' &&
  typeof Reflect.get(value, 'superlike') === 'boolean';

/** The cached flags: `undefined` when never stored (or unreadable). */
const readStoredStartFeatures = (): StartTmaFeatures | null | undefined => {
  try {
    const raw = window.localStorage.getItem(START_FEATURES_STORAGE_KEY);

    if (raw === null) return undefined;

    const value: unknown = JSON.parse(raw);

    if (value === null || isStartFeatures(value)) return value;

    return undefined;
  } catch {
    return undefined;
  }
};

const storeStartFeatures = (features: StartTmaFeatures | null) => {
  try {
    window.localStorage.setItem(
      START_FEATURES_STORAGE_KEY,
      JSON.stringify(features),
    );
  } catch {
    // Private mode or a full storage: the flags stay for this session only.
  }
};

const waitFor = (ms: number) =>
  new Promise<null>((resolve) => {
    setTimeout(() => resolve(null), ms);
  });

const readStoredTmaAppInstanceId = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.localStorage.getItem(APP_INSTANCE_ID_STORAGE_KEY);
};

const storeTmaAppInstanceId = (appInstanceId: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(APP_INSTANCE_ID_STORAGE_KEY, appInstanceId);
};

const clearStoredTmaAppInstanceId = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.removeItem(APP_INSTANCE_ID_STORAGE_KEY);
};

const requestTmaAppInstanceId = async (): Promise<string> => {
  const initData = await waitForTmaInitData();

  if (!initData) {
    throw new Error('No supported app instance surface detected');
  }

  const response = await fetcher<PostStartTmaResponse>({
    url: '/start/tma',
    method: 'POST',
    data: {
      name: TMA_APP_INSTANCE_NAME,
      initData,
    },
    auth: null,
  });

  const features = response?.features;

  startFeatures = isStartFeatures(features) ? features : null;
  storeStartFeatures(startFeatures);

  const appInstanceId = response?.appInstance?.id;

  if (!appInstanceId) {
    throw new Error('Start TMA response missing app instance id');
  }

  appInstanceIdCache = appInstanceId;
  storeTmaAppInstanceId(appInstanceId);
  return appInstanceId;
};

const startTmaAppInstanceRequest = (): Promise<string> => {
  startPromise = requestTmaAppInstanceId().finally(() => {
    startPromise = null;
  });

  return startPromise;
};

export const appInstanceService = {
  getCurrentPaymentProvider: (): 'telegram_stars' | null => {
    if (hasTmaInitData()) {
      return 'telegram_stars';
    }

    return null;
  },

  getCachedTmaAppInstanceId: () => {
    if (appInstanceIdCache) {
      return appInstanceIdCache;
    }

    const storedAppInstanceId = readStoredTmaAppInstanceId();

    if (storedAppInstanceId) {
      appInstanceIdCache = storedAppInstanceId;

      return storedAppInstanceId;
    }

    return null;
  },

  clearCachedTmaAppInstanceId: () => {
    appInstanceIdCache = null;
    clearStoredTmaAppInstanceId();
  },

  getOrCreateCurrentAppInstanceId: async (): Promise<string> => {
    const cached = appInstanceService.getCachedTmaAppInstanceId();
    if (cached) {
      return cached;
    }

    if (startPromise) {
      return startPromise;
    }

    return startTmaAppInstanceRequest();
  },

  getOrCreateTmaAppInstanceId: async (): Promise<string> => {
    return appInstanceService.getOrCreateCurrentAppInstanceId();
  },

  refreshTmaAppInstanceId: async (): Promise<string> => {
    appInstanceService.clearCachedTmaAppInstanceId();

    return startTmaAppInstanceRequest();
  },

  /**
   * The flags of the last `/start/tma` answer, read synchronously: this
   * session's, else the stored ones. `undefined`: never asked; `null`:
   * asked, and the answer had no flags (an older backend).
   */
  getStartFeatures: (): StartTmaFeatures | null | undefined =>
    startFeatures !== undefined ? startFeatures : readStoredStartFeatures(),

  /**
   * The global flags for the sign-up's role step. A launch with a stored app
   * instance id skips `/start/tma`, so this asks it once per session. The
   * call is safe to repeat: the server answers the same Telegram user's
   * reusable instance (and refreshes its delivery endpoint), which is stored
   * as on a first launch. Only the sign-up asks, so a registered user's
   * launch makes no extra request.
   *
   * - This session's answer: returned at once, no request.
   * - Stored flags: returned at once; the request refreshes them for the
   *   next launch in the background.
   * - Nothing stored: waits for the answer at most `START_FEATURES_WAIT_MS`,
   *   then answers `null` (flags off, no role step), so a hung request never
   *   blocks the sign-up.
   */
  loadStartFeatures: async (): Promise<StartTmaFeatures | null> => {
    if (startFeatures !== undefined) {
      return startFeatures;
    }

    const answer = (startPromise ?? startTmaAppInstanceRequest()).then(
      () => startFeatures ?? null,
      () => null,
    );
    const stored = readStoredStartFeatures();

    if (stored !== undefined) {
      return stored;
    }

    return Promise.race([answer, waitFor(START_FEATURES_WAIT_MS)]);
  },
};
